import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getMemberOnboardingStatus } from "@/frontend/lib/member-utils";
import { useSession } from "@/frontend/lib/auth";
import { useBillingLockStore } from "@/frontend/stores/billingLockStore";
import { getOrganizationBillingPath } from "@/shared/billing-path";

type RouteStatus =
  | { state: "loading" | "incomplete" | "error" }
  | { state: "complete"; billingLocked: boolean; trialAlreadyUsed: boolean; canPay: boolean };

export function CompletedOnboardingRoute({ children }: { children: ReactNode }) {
  const { orgId } = useParams<{ orgId: string }>();
  const { pathname } = useLocation();
  const { data: session } = useSession();
  const [status, setStatus] = useState<RouteStatus>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);
  const userCompleted = session?.user.onboardingCompleted === true;
  const setBillingLock = useBillingLockStore((state) => state.setBillingLock);

  useEffect(() => {
    let cancelled = false;

    if (!orgId) {
      setStatus({ state: "incomplete" });
      return;
    }

    // Asked even when the user finished onboarding: a workspace whose no-card trial ended is
    // locked for everyone in it until it pays.
    setStatus({ state: "loading" });
    void getMemberOnboardingStatus(orgId).then((result) => {
      if (cancelled) return;
      setBillingLock({ billingLocked: result?.billingLocked === true, trialAlreadyUsed: result?.trialAlreadyUsed === true });
      if (result === null) {
        // Established users keep working if the check is unavailable
        setStatus(userCompleted ? { state: "complete", billingLocked: false, trialAlreadyUsed: false, canPay: false } : { state: "error" });
        return;
      }
      setStatus(
        result.onboardingCompleted || userCompleted
          ? {
              state: "complete",
              billingLocked: result.billingLocked === true,
              trialAlreadyUsed: result.trialAlreadyUsed === true,
              canPay: result.role === "owner" || result.role === "admin",
            }
          : { state: "incomplete" }
      );
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, attempt, userCompleted, setBillingLock]);

  if (status.state === "incomplete") {
    return <Navigate to="/onboarding/name" replace />;
  }

  if (status.state === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">We couldn't verify your setup</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Check your connection and try again. Product access stays locked until setup is verified.
          </p>
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
            className="mt-5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (status.state === "loading") {
    // Established users see the app while the trial lock is checked; it only switches if locked
    if (userCompleted) return children;
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Verifying setup…
      </div>
    );
  }

  if (status.billingLocked && orgId) {
    const billingPath = getOrganizationBillingPath(orgId);
    if (status.canPay) {
      return pathname === billingPath ? children : <Navigate to={billingPath} replace />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">
            {status.trialAlreadyUsed ? "This workspace needs a plan" : "This workspace's free trial has ended"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {status.trialAlreadyUsed
              ? "The email that created it has already used Querygen's free trial. Ask a workspace admin to choose a plan."
              : "Ask a workspace admin to choose a plan to keep using Querygen."}
          </p>
        </div>
      </div>
    );
  }

  return children;
}
